import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../Components/LanguageContext';
import {
  fetchAuthorizedServiceBrands,
  fetchAuthorizedServicesByBrand,
} from '../api/publicApi';
import handleTransformLang from '../functions/handleTransformLang';
import Layout from '../Layout';
import supportAuthorizedServicePage from '../lang/pages/supportAuthorizedService.json';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const INPUT_CLASS =
  'block h-full w-full appearance-none border-0 bg-transparent px-6 text-[17px] text-text_secondary transition placeholder:text-[#95a0b1] focus:border-transparent focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0';

const SEARCH_WRAPPER_CLASS =
  'group/search flex h-[3.85rem] overflow-hidden border border-[#b7beca] bg-white transition focus-within:border-bg_secondary focus-within:ring-1 focus-within:ring-bg_secondary';

const normalizeString = (value) => String(value ?? '').trim();

const normalizeBrandList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const arrayKeys = ['data', 'items', 'results', 'result', 'brands', 'content'];

  for (const key of arrayKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  for (const key of arrayKeys) {
    if (payload?.[key] && typeof payload[key] === 'object') {
      const nested = normalizeBrandList(payload[key]);
      if (nested.length > 0) return nested;
    }
  }

  if (payload?.brandId !== undefined || payload?.brandName !== undefined) {
    return [payload];
  }

  return [];
};

const normalizeServiceCategoryList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const arrayKeys = ['data', 'items', 'results', 'result', 'categories', 'content'];

  for (const key of arrayKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  for (const key of arrayKeys) {
    if (payload?.[key] && typeof payload[key] === 'object') {
      const nested = normalizeServiceCategoryList(payload[key]);
      if (nested.length > 0) return nested;
    }
  }

  if (payload?.categoryId !== undefined || payload?.categoryName !== undefined) {
    return [payload];
  }

  return [];
};

const mapBrand = (item, index) => ({
  brandId: Number(item?.brandId ?? item?.id ?? index + 1),
  brandName: normalizeString(item?.brandName ?? item?.name),
});

const flattenServiceRows = (payload, brandName) =>
  normalizeServiceCategoryList(payload).flatMap((category, categoryIndex) => {
    const categoryName = normalizeString(
      category?.categoryName ?? category?.name ?? brandName
    );
    const addresses = Array.isArray(category?.addresses) ? category.addresses : [];

    return addresses
      .map((address, addressIndex) => {
        const addressName = normalizeString(
          address?.addressName ?? address?.name ?? categoryName
        );
        const addressLine = [normalizeString(address?.address1), normalizeString(address?.address2)]
          .filter(Boolean)
          .join(' ');

        return {
          id: `${categoryName}-${addressName}-${categoryIndex}-${addressIndex}`,
          serviceName: addressName || categoryName || brandName,
          addressLine,
          phone1: normalizeString(address?.phone1 ?? address?.telephone ?? address?.phone),
          phone2: normalizeString(address?.phone2),
          email: normalizeString(
            address?.email ?? address?.mail ?? address?.email1 ?? address?.eMail
          ),
        };
      })
      .filter(
        (item) =>
          item.serviceName.length > 0 ||
          item.addressLine.length > 0 ||
          item.phone1.length > 0 ||
          item.phone2.length > 0 ||
          item.email.length > 0
      );
  });

export default function SupportAuthorizedService() {
  return (
    <Layout headData={supportAuthorizedServicePage.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);
  const [pageContent, setPageContent] = useState(
    handleTransformLang(supportAuthorizedServicePage)
  );
  const [brandQuery, setBrandQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [lastFetchedPrefix, setLastFetchedPrefix] = useState('');
  const [brands, setBrands] = useState([]);
  const [isBrandLoading, setIsBrandLoading] = useState(false);
  const [brandError, setBrandError] = useState('');
  const [openBrandId, setOpenBrandId] = useState(null);
  const [serviceCache, setServiceCache] = useState({});

  useEffect(() => {
    setPageContent(handleTransformLang(supportAuthorizedServicePage));
  }, [language]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(brandQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [brandQuery]);

  useEffect(() => {
    let mounted = true;
    const normalizedQuery = debouncedQuery.toLocaleLowerCase('tr-TR');

    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setBrands([]);
      setIsBrandLoading(false);
      setBrandError('');
      setLastFetchedPrefix('');
      return undefined;
    }

    const nextPrefix = normalizedQuery.slice(0, MIN_QUERY_LENGTH);
    if (nextPrefix === lastFetchedPrefix) {
      return undefined;
    }

    const loadBrands = async () => {
      setIsBrandLoading(true);
      setBrandError('');

      try {
        const payload = await fetchAuthorizedServiceBrands(nextPrefix);
        if (!mounted) return;

        const nextBrands = normalizeBrandList(payload)
          .map(mapBrand)
          .filter(
            (item) =>
              Number.isFinite(item.brandId) &&
              normalizeString(item.brandName).length > 0
          );

        setBrands(nextBrands);
        setLastFetchedPrefix(nextPrefix);
      } catch {
        if (!mounted) return;
        setBrands([]);
        setLastFetchedPrefix('');
        setBrandError(pageContent.states.brandSearchError);
      } finally {
        if (mounted) setIsBrandLoading(false);
      }
    };

    loadBrands();

    return () => {
      mounted = false;
    };
  }, [debouncedQuery, lastFetchedPrefix, pageContent.states.brandSearchError]);

  const visibleBrands = useMemo(() => {
    const normalizedQuery = brandQuery.trim().toLocaleLowerCase('tr-TR');
    if (normalizedQuery.length < MIN_QUERY_LENGTH) return [];

    return brands.filter((brand) =>
      normalizeString(brand.brandName)
        .toLocaleLowerCase('tr-TR')
        .includes(normalizedQuery)
    );
  }, [brandQuery, brands]);

  const handleSearch = () => {
    const trimmedQuery = brandQuery.trim();
    if (trimmedQuery.length < MIN_QUERY_LENGTH) return;

    setDebouncedQuery(trimmedQuery);
  };

  const handleBrandChange = (nextValue) => {
    setBrandQuery(nextValue);
    setBrandError('');
    setOpenBrandId(null);
  };

  const handleToggleBrand = async (brand) => {
    if (openBrandId === brand.brandId) {
      setOpenBrandId(null);
      return;
    }

    setOpenBrandId(brand.brandId);

    if (serviceCache[brand.brandId]?.isLoaded || serviceCache[brand.brandId]?.isLoading) {
      return;
    }

    setServiceCache((current) => ({
      ...current,
      [brand.brandId]: {
        isLoading: true,
        isLoaded: false,
        error: '',
        rows: [],
      },
    }));

    try {
      const payload = await fetchAuthorizedServicesByBrand(brand.brandId);
      const rows = flattenServiceRows(payload, brand.brandName);

      setServiceCache((current) => ({
        ...current,
        [brand.brandId]: {
          isLoading: false,
          isLoaded: true,
          error: '',
          rows,
        },
      }));
    } catch {
      setServiceCache((current) => ({
        ...current,
        [brand.brandId]: {
          isLoading: false,
          isLoaded: false,
          error: pageContent.states.serviceError,
          rows: [],
        },
      }));
    }
  };

  return (
    <section className="bg-white pb-28 pt-10 lg:pb-32 lg:pt-12">
      <div className="container mx-auto max-w-8xl px-4">
        <div className="flex items-start justify-between gap-8">
          <Link to="/" className="inline-flex">
            <img
              src="/images/logo.svg"
              alt="Bulutmarket"
              className="h-24 w-auto lg:h-28"
            />
          </Link>
        </div>

        <div className="mx-auto mt-24 max-w-[74rem] text-center lg:mt-32">
          <h1 className="text-[32px] font-bold leading-tight text-text_primary">
            {pageContent.title}
          </h1>

          <p className="mx-auto mt-8 max-w-[66rem] text-[17px] leading-[1.75] text-text_secondary">
            {pageContent.description}
          </p>

          <div className="mx-auto mt-24 max-w-[65.25rem] text-left">
            <div className={SEARCH_WRAPPER_CLASS}>
              <input
                type="text"
                maxLength={100}
                value={brandQuery}
                placeholder={pageContent.placeholder}
                className={INPUT_CLASS}
                onChange={(event) => handleBrandChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
              />

              <button
                type="button"
                className="group/button relative h-full w-[4.72rem] shrink-0 focus:outline-none focus-visible:outline-none"
                aria-label={pageContent.buttonLabel}
                onClick={handleSearch}
              >
                <img
                  src="/images/support/searchbox_zemin.svg"
                  alt=""
                  className="absolute inset-0 h-full w-full object-fill transition-opacity duration-200 group-hover/button:opacity-0 group-focus-within/search:opacity-0"
                />
                <img
                  src="/images/support/searchbox_zemin_active.svg"
                  alt=""
                  className="absolute inset-0 h-full w-full object-fill opacity-0 transition-opacity duration-200 group-hover/button:opacity-100 group-focus-within/search:opacity-100"
                />
                <img
                  src="/images/support/search_box_icon.svg"
                  alt=""
                  className="absolute left-1/2 top-1/2 z-10 h-7 w-7 -translate-x-1/2 -translate-y-1/2 transition-all duration-200 group-hover/button:opacity-0 group-focus-within/search:opacity-0"
                />
                <img
                  src="/images/support/search_box_icon_active.svg"
                  alt=""
                  className="absolute left-1/2 top-1/2 z-10 h-7 w-7 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-all duration-200 group-hover/button:scale-[1.05] group-hover/button:opacity-100 group-focus-within/search:scale-[1.05] group-focus-within/search:opacity-100"
                />
              </button>
            </div>

            {brandError ? (
              <p className="mt-3 text-[15px] text-[#d56152]">{brandError}</p>
            ) : null}

            {brandQuery.trim().length > 0 && brandQuery.trim().length < MIN_QUERY_LENGTH ? (
              <div className="mt-4 bg-[#f5f5f6] px-5 py-4 text-[17px] text-text_secondary">
                {pageContent.states.searchHint}
              </div>
            ) : null}

            {brandQuery.trim().length >= MIN_QUERY_LENGTH ? (
              <div className="mt-4 space-y-[2px] bg-white">
                {isBrandLoading ? (
                  <div className="bg-[#f5f5f6] px-5 py-5 text-[17px] text-text_secondary">
                    {pageContent.states.loadingBrands}
                  </div>
                ) : visibleBrands.length === 0 ? (
                  <div className="bg-[#f5f5f6] px-5 py-5 text-[17px] text-text_secondary">
                    {pageContent.states.emptyBrands}
                  </div>
                ) : (
                  visibleBrands.map((brand) => {
                    const isOpen = openBrandId === brand.brandId;
                    const serviceState = serviceCache[brand.brandId];

                    return (
                      <div key={brand.brandId} className="overflow-hidden">
                        <button
                          type="button"
                          onClick={() => handleToggleBrand(brand)}
                          className="flex w-full items-center bg-[#f5f5f6] px-5 py-[1.05rem] text-left text-[17px] font-bold text-text_primary transition-colors hover:bg-[#ededee]"
                        >
                          {brand.brandName}
                        </button>

                        {isOpen ? (
                          <div className="bg-white px-5 py-5">
                            {serviceState?.isLoading ? (
                              <p className="text-[17px] text-text_secondary">
                                {pageContent.states.loadingServices}
                              </p>
                            ) : serviceState?.error ? (
                              <p className="text-[17px] text-[#d56152]">
                                {serviceState.error}
                              </p>
                            ) : serviceState?.rows?.length ? (
                              <div className="space-y-6">
                                {serviceState.rows.map((service, index) => (
                                  <div
                                    key={service.id}
                                    className={`pb-6 ${
                                      index !== serviceState.rows.length - 1
                                        ? 'border-b border-[#eceef3]'
                                        : ''
                                    }`}
                                  >
                                    <h2 className="text-[17px] font-bold uppercase text-text_primary">
                                      {service.serviceName}
                                    </h2>

                                    <div className="mt-4 space-y-4 text-[17px] text-text_secondary">
                                      {service.addressLine ? (
                                        <div className="flex items-start gap-3">
                                          <img
                                            src="/images/support/adres.svg"
                                            alt=""
                                            className="mt-1 h-[1.15rem] w-[1.15rem] shrink-0"
                                          />
                                          <p className="leading-7">{service.addressLine}</p>
                                        </div>
                                      ) : null}

                                      <div className="flex flex-wrap gap-x-10 gap-y-3">
                                        {service.phone1 ? (
                                          <div className="flex items-center gap-3">
                                            <img
                                              src="/images/support/telefon.svg"
                                              alt=""
                                              className="h-[1.05rem] w-[1.05rem] shrink-0"
                                            />
                                            <span>{service.phone1}</span>
                                          </div>
                                        ) : null}

                                        {service.phone2 ? (
                                          <div className="flex items-center gap-3">
                                            <img
                                              src="/images/support/telefon.svg"
                                              alt=""
                                              className="h-[1.05rem] w-[1.05rem] shrink-0"
                                            />
                                            <span>{service.phone2}</span>
                                          </div>
                                        ) : null}

                                        {service.email ? (
                                          <div className="flex items-center gap-3">
                                            <img
                                              src="/images/support/mail.svg"
                                              alt=""
                                              className="h-[0.95rem] w-[1.1rem] shrink-0"
                                            />
                                            <span>{service.email}</span>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-[17px] text-text_secondary">
                                {pageContent.states.emptyServices}
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
